import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@/contexts/themeContext';
import styles from './Sidebar.module.scss';
import MenuList from '../MenuList';

import Logo from '@/assets/images/logo_2.png';
import { Button } from 'antd';

import { Scrollbars } from 'rc-scrollbars';
import type { ScrollValues } from 'rc-scrollbars';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import QuickLinks from '@/components/QuickLinks';

/** Tolerance in px before we treat the scroller as "at an edge". */
const EDGE_EPSILON = 2;

/** antd needs a frame or two to finish the submenu expand animation before we can measure. */
const REVEAL_DELAY_MS = 260;

const renderThumb = ({ style, className, ...props }: any) => (
  <div
    className={`${className ?? ''} ${styles.scrollThumb}`}
    style={style}
    {...props}
  />
);

/**
 * The scrollbar is the primary affordance here: unlike a fade it reports how
 * much more nav there is and where you are in it. Two of rc-scrollbars' inline
 * styles are dropped so it can do that job:
 *
 * - `display` — set to `none` whenever the OS reports a zero-width scrollbar
 *   (every Mac, which has overlay scrollbars), which would otherwise hide the
 *   track entirely on those machines. `hideTracksWhenNotNeeded` still hides it
 *   via `visibility` when there is genuinely nothing to scroll.
 * - `width` — hardcoded to 6px, too thin to notice against a dark sidebar.
 *
 * Only the track's width changes; rc-scrollbars measures the thumb off the
 * track's height, so its drag maths is untouched.
 */
const renderTrack = ({ style, className, ...props }: any) => {
  const trackStyle = { ...(style ?? {}) };
  delete trackStyle.display;
  delete trackStyle.width;

  return (
    <div
      className={`${className ?? ''} ${styles.scrollTrack}`}
      style={trackStyle}
      {...props}
    />
  );
};

const Sidebar = () => {
  const { sidebarCollapseState, toggleSidebarState } = useTheme();
  const location = useLocation();

  const scrollbarsRef = useRef<Scrollbars>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  const toggleCollapsed = () => {
    toggleSidebarState();
  };

  // Fades the last row where the list clips — peripheral reinforcement for the
  // scrollbar, which carries the actual signal. Only the bottom edge needs it:
  // the top is either covered by a pinned section header or, with nothing
  // expanded, sits flush under the logo where there is nothing to mistake.
  //
  // Bails out when the value is unchanged: onUpdate fires from the scroller's
  // own componentDidUpdate, so an unconditional setState would loop.
  const handleScrollValues = useCallback((values: ScrollValues) => {
    const { scrollTop, scrollHeight, clientHeight } = values;
    const more = scrollTop + clientHeight < scrollHeight - EDGE_EPSILON;

    setHasMoreBelow((prev) => (prev === more ? prev : more));
  }, []);

  // Deep-linking to a route whose menu item sits below the fold used to leave
  // the sidebar scrolled to the top with the highlight off-screen.
  useEffect(() => {
    if (sidebarCollapseState) return;

    const timer = window.setTimeout(() => {
      const instance = scrollbarsRef.current;
      const view = instance?.view;
      const active = view?.querySelector<HTMLElement>(
        '.ant-menu-item-selected',
      );
      if (!instance || !view || !active) return;

      const viewRect = view.getBoundingClientRect();
      const itemRect = active.getBoundingClientRect();
      const isVisible =
        itemRect.top >= viewRect.top && itemRect.bottom <= viewRect.bottom;
      if (isVisible) return;

      const delta =
        itemRect.top - viewRect.top - (viewRect.height - itemRect.height) / 2;
      instance.scrollTop(instance.getScrollTop() + delta);
    }, REVEAL_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [location.pathname, sidebarCollapseState]);

  return (
    <div
      className={`sidebar-container ${styles['sidebar-container']} ${sidebarCollapseState ? styles.sidebarContainerCollapse : ''}`}
    >
      <div className={styles.sidebar}>
        <div
          className={styles['sidebar-logo-container']}
          style={{
            justifyContent: sidebarCollapseState ? 'center' : 'space-between',
          }}
        >
          <img
            src={Logo}
            style={{
              width: sidebarCollapseState ? '0%' : '150px',
              paddingRight: sidebarCollapseState ? '0' : '1rem',
            }}
            alt="Broker Copilot"
          />
          <Button
            onClick={toggleCollapsed}
            type="text"
            icon={
              sidebarCollapseState ? (
                <MenuUnfoldOutlined
                  style={{ fontSize: '18px', color: '#fff' }}
                />
              ) : (
                <MenuFoldOutlined style={{ fontSize: '22px', color: '#fff' }} />
              )
            }
          />
        </div>
        <div
          className={`${styles.scrollArea} ${hasMoreBelow ? styles.fadeBottom : ''}`}
        >
          <Scrollbars
            ref={scrollbarsRef}
            autoHide={false}
            hideTracksWhenNotNeeded
            renderThumbHorizontal={renderThumb}
            renderThumbVertical={renderThumb}
            renderTrackVertical={renderTrack}
            onUpdate={handleScrollValues}
            onScrollFrame={handleScrollValues}
          >
            <div className={styles['sidebar-body']}>
              <MenuList />
            </div>
          </Scrollbars>
        </div>
      </div>
      <div
        style={{
          display: sidebarCollapseState ? 'none' : 'flex',
          transition: 'all 0.5s ease',
        }}
        className={styles['sidebar-bottom-container']}
      >
        <QuickLinks />
      </div>
    </div>
  );
};

export default Sidebar;
